import React, { useMemo, useState } from 'react';
import { Drawer } from 'antd';
import { motion } from 'framer-motion';
import {
  DEPARTMENTS,
  type ChangeItem,
  type ChangeTag,
  type Department,
  type ReleaseEntry,
} from './whatsNewTypes';
import { useReleases } from './useVersionHistory';
import './whatsNew.css';

/**
 * "What's New" — a calm, audit-friendly TIMELINE. Newest first, version-wise,
 * in very small type. Each release is a node on a vertical rail; under it, the
 * changes it shipped, grouped department → module and tagged New / Improved /
 * Fixed. Reference content, not an announcement — no decorative icons.
 */

// ── Sovereign Emerald tokens (local, matching DynamicSidebar). ───────────────
const SE = {
  paper: '#F2EADB',
  header: '#F8F2E2',
  hairline: 'rgba(11,59,46,0.14)',
  hairlineSoft: 'rgba(11,59,46,0.12)',
  rail: 'rgba(11,59,46,0.16)',
  ink: '#2A1F17',
  title: '#0B3B2E',
  bottle: '#1F5A47',
  champagne: '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneRing: 'rgba(201,169,97,0.50)',
  muted: '#7C8A82',
} as const;

const TAG_STYLE: Record<ChangeTag, { label: string; fg: string; bg: string }> = {
  new: { label: 'New', fg: '#0B3B2E', bg: 'rgba(51,146,116,0.14)' },
  improved: { label: 'Improved', fg: '#8A6D2B', bg: 'rgba(201,169,97,0.18)' },
  fixed: { label: 'Fixed', fg: '#5B6B63', bg: 'rgba(11,59,46,0.07)' },
};

type Filter = Department | 'All';

const DEPT_ORDER: Department[] = ['Platform', 'OMS', 'AMS', 'EPC'];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function isVisible(change: ChangeItem, filter: Filter): boolean {
  if (filter === 'All') return true;
  if (filter === 'Platform') return change.department === 'Platform';
  return change.department === filter || change.department === 'Platform';
}

function metaLine(change: ChangeItem): string {
  const parts: string[] = [];
  if (change.appliesTo && change.appliesTo !== 'both') {
    parts.push(change.appliesTo === 'solar' ? 'Solar' : 'Wind');
  }
  if (change.sites) parts.push(change.sites);
  if (change.obligation) parts.push(`Reporting: ${change.obligation}`);
  return parts.join(' · ');
}

function TagChip({ tag }: { tag: ChangeTag }) {
  const s = TAG_STYLE[tag];
  return (
    <span
      className="inline-block rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.05em] align-middle"
      style={{ color: s.fg, backgroundColor: s.bg }}
    >
      {s.label}
    </span>
  );
}

function ChangeRow({ change }: { change: ChangeItem }) {
  const meta = metaLine(change);
  return (
    <li className="leading-snug">
      <p className="text-[9px] uppercase font-bold tracking-[0.11em]" style={{ color: SE.muted }}>
        {change.module}
      </p>
      <p className="text-[11px] leading-snug mt-[1px]" style={{ color: SE.ink }}>
        <TagChip tag={change.tag} />{' '}
        <span style={{ fontWeight: 600 }}>{change.summary}</span>
      </p>
      {change.detail && (
        <p className="text-[10px] leading-snug mt-[1px]" style={{ color: SE.muted }}>
          {change.detail}
        </p>
      )}
      {change.restatesFigures && (
        <p
          className="text-[10px] font-semibold mt-[2px] inline-block rounded px-1.5 py-[1px]"
          style={{ color: '#7A5410', backgroundColor: 'rgba(224,168,60,0.16)' }}
        >
          Restates previously reported figures — see entry detail.
        </p>
      )}
      {meta && (
        <p className="text-[9.5px] mt-[1px]" style={{ color: SE.muted }}>
          {meta}
        </p>
      )}
    </li>
  );
}

function ReleaseNode({
  release,
  filter,
  index,
}: {
  release: ReleaseEntry;
  filter: Filter;
  index: number;
}) {
  const visible = release.changes.filter((c) => isVisible(c, filter));
  if (visible.length === 0) return null;

  const byDept = DEPT_ORDER.map((dept) => ({
    dept,
    items: visible.filter((c) => c.department === dept),
  })).filter((g) => g.items.length > 0);

  const dateLabel = formatDate(release.date);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.16) }}
      className="relative pl-5 pb-5"
    >
      {/* Node dot on the rail */}
      <span
        aria-hidden
        className="absolute top-[3px] w-[9px] h-[9px] rounded-full"
        style={{
          left: 2,
          backgroundColor: SE.champagne,
          boxShadow: `0 0 0 2px ${SE.paper}, inset 0 0 0 1px ${SE.champagneRing}`,
        }}
      />

      {/* Date-led header + version + high-level headline */}
      <div className="mb-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          {release.draft ? (
            <span
              className="text-[10px] font-semibold rounded px-1 py-[1px]"
              style={{ color: '#7A5410', backgroundColor: 'rgba(224,168,60,0.16)' }}
            >
              Draft — confirm date
            </span>
          ) : (
            dateLabel && (
              <span className="text-[11.5px] font-bold tracking-tight" style={{ color: SE.title }}>
                {dateLabel}
              </span>
            )
          )}
          <span
            className="text-[9.5px] font-semibold rounded px-1.5 py-[1px]"
            style={{
              color: SE.title,
              backgroundColor: SE.champagneWash,
              boxShadow: 'inset 0 0 0 1px rgba(201,169,97,0.45)',
            }}
          >
            {release.version}
          </span>
          {release.channel && (
            <span className="text-[9.5px]" style={{ color: SE.bottle }}>
              · {release.channel}
            </span>
          )}
          {release.status && release.status !== 'Released' && (
            <span className="text-[9.5px]" style={{ color: SE.muted }}>
              · {release.status}
            </span>
          )}
        </div>
        {release.headline && (
          <p className="text-[11.5px] font-medium leading-snug mt-1" style={{ color: SE.ink }}>
            {release.headline}
          </p>
        )}
      </div>

      {/* Changes grouped by department */}
      <div className="space-y-2.5">
        {byDept.map((group) => (
          <div key={group.dept}>
            <p
              className="text-[9px] uppercase font-bold tracking-[0.13em] mb-1"
              style={{ color: SE.bottle }}
            >
              {group.dept}
            </p>
            <ul className="space-y-2">
              {group.items.map((c, i) => (
                <ChangeRow key={`${c.module}-${i}`} change={c} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </motion.section>
  );
}

export interface WhatsNewDrawerProps {
  open: boolean;
  onClose: () => void;
  activeDepartment?: string | null;
}

const WhatsNewDrawer: React.FC<WhatsNewDrawerProps> = ({
  open,
  onClose,
  activeDepartment,
}) => {
  const defaultFilter: Filter = useMemo(() => {
    const dep = (activeDepartment ?? '').toUpperCase();
    return (['OMS', 'AMS', 'EPC'] as const).includes(dep as any)
      ? (dep as Department)
      : 'All';
  }, [activeDepartment]);

  const [filter, setFilter] = useState<Filter>(defaultFilter);

  React.useEffect(() => {
    if (open) setFilter(defaultFilter);
  }, [open, defaultFilter]);

  const { data: releases = [], isLoading, isError } = useReleases();

  const filterOptions: Filter[] = ['All', ...DEPARTMENTS];
  const anyVisible = releases.some((r) => r.changes.some((c) => isVisible(c, filter)));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={620}
      closable={false}
      rootClassName="whats-new-drawer"
      styles={{
        mask: { backgroundColor: 'rgba(11,59,46,0.28)' },
        header: { display: 'none' },
        body: { padding: 0, backgroundColor: SE.paper },
        content: { backgroundColor: SE.paper },
        wrapper: { boxShadow: '8px 0 40px rgba(11,59,46,0.18)' },
      }}
    >
      <div className="flex flex-col h-full" style={{ color: SE.ink }}>
        {/* Header — text only, no decorative icon */}
        <div
          className="px-4 pt-3.5 pb-2.5"
          style={{ backgroundColor: SE.header, borderBottom: `1px solid ${SE.hairline}` }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2
                className="text-[13px] font-bold tracking-tight leading-tight"
                style={{ color: SE.title }}
              >
                What&rsquo;s New &amp; Updates
              </h2>
              <p className="text-[10px] tracking-tight mt-[1px]" style={{ color: SE.muted }}>
                Release timeline — newest first
              </p>
            </div>

            <div className="flex items-center gap-2 whats-new-no-print">
              <button
                type="button"
                onClick={() => window.print()}
                className="text-[10.5px] font-semibold rounded px-1.5 py-[2px] transition-colors"
                style={{ color: SE.muted, boxShadow: 'inset 0 0 0 1px rgba(11,59,46,0.14)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = SE.title;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = SE.muted;
                }}
              >
                Print
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="text-[13px] leading-none px-1 transition-colors"
                style={{ color: SE.muted }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = SE.title;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = SE.muted;
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Department filter — small text pills */}
          <div className="mt-2.5 flex flex-wrap items-center gap-1 whats-new-no-print">
            {filterOptions.map((opt) => {
              const active = filter === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setFilter(opt)}
                  className="text-[10px] font-semibold rounded-full px-2 py-[2px] transition-all"
                  style={{
                    color: active ? SE.title : SE.muted,
                    backgroundColor: active ? SE.champagneWash : 'transparent',
                    boxShadow: active
                      ? 'inset 0 0 0 1px rgba(201,169,97,0.45)'
                      : 'inset 0 0 0 1px rgba(11,59,46,0.12)',
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto no-scrollbar px-4 pt-4">
          {isLoading ? (
            <p className="text-[11px] py-6 text-center" style={{ color: SE.muted }}>
              Loading updates…
            </p>
          ) : isError ? (
            <p className="text-[11px] py-6 text-center" style={{ color: SE.muted }}>
              Couldn&rsquo;t load updates — please try again.
            </p>
          ) : releases.length === 0 ? (
            <p className="text-[11px] py-6 text-center" style={{ color: SE.muted }}>
              No updates yet.
            </p>
          ) : anyVisible ? (
            <div className="relative">
              {/* Vertical rail */}
              <span
                aria-hidden
                className="absolute top-1 bottom-2 w-px"
                style={{ left: 6, backgroundColor: SE.rail }}
              />
              {releases.map((r, i) => (
                <ReleaseNode key={`${r.version}-${i}`} release={r} filter={filter} index={i} />
              ))}
            </div>
          ) : (
            <p className="text-[11px] py-6 text-center" style={{ color: SE.muted }}>
              No updates for this view.
            </p>
          )}
        </div>

        {/* Footer note */}
        <div
          className="px-4 py-2 text-[9.5px] whats-new-no-print"
          style={{
            borderTop: `1px solid ${SE.hairline}`,
            color: SE.muted,
            backgroundColor: SE.header,
          }}
        >
          You see updates for your department and sites, plus shared changes that affect you.
        </div>
      </div>
    </Drawer>
  );
};

export default WhatsNewDrawer;
